import { Button } from '@shared/components';
import { useToast } from '@shared/components/toast';
import { MessageRatingFragment } from '@shared/generated/graphql';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import clsx from 'clsx';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { RefObject, useRef, useState } from 'react';
import {
  MessageRatingDetails,
  useMessageFeedback,
} from '../../hooks/useMessageFeedback';
import { MessageFeedbackModal } from './MessageFeedbackModal';

type MessageFeedbackProps = {
  messageId: string;
  conversationId?: string;
  rating?: MessageRatingFragment;
  isAlwaysVisible: boolean;
  isDisabled?: boolean;
};

export function MessageFeedback({
  messageId,
  conversationId,
  rating,
  isAlwaysVisible,
  isDisabled = false,
}: MessageFeedbackProps) {
  const { rate, remove, saveDetails } = useMessageFeedback(messageId);
  const { track } = useAnalytics();
  const { sendToast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // NOTE(aman): Kept apart from isDialogOpen so the title holds while the
  // dialog fades out.
  const [isDialogPositive, setIsDialogPositive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingRateRef = useRef<Promise<void> | undefined>(undefined);
  const goodButtonRef = useRef<HTMLButtonElement>(null);
  const badButtonRef = useRef<HTMLButtonElement>(null);

  const handleThumbPress = (isPositive: boolean) => {
    if (rating?.isPositive === isPositive) {
      remove().catch(() =>
        sendToast("Couldn't remove your rating", { variant: 'error' })
      );
      return;
    }
    const ratingSaved = rate(isPositive, rating);
    pendingRateRef.current = ratingSaved;
    ratingSaved
      .then(() => {
        track('Rated Chat Message', {
          conversationId,
          data: { messageId, isPositive },
        });
      })
      .catch(() =>
        sendToast("Couldn't save your rating", { variant: 'error' })
      );
    setIsDialogPositive(isPositive);
    setIsDialogOpen(true);
  };

  const sendDetails = async (
    details: MessageRatingDetails
  ): Promise<boolean> => {
    const comments = details.comments.trim();
    if (!comments && !details.reason) return true;
    try {
      // NOTE(aman): Details attach to the saved rating, so wait for it and
      // retry it if the click's save failed.
      await pendingRateRef.current?.catch(() => rate(isDialogPositive, rating));
      await saveDetails({ reason: details.reason, comments });
    } catch {
      sendToast("Couldn't send your feedback", { variant: 'error' });
      return false;
    }
    track('Submitted Chat Message Feedback', {
      conversationId,
      data: {
        messageId,
        isPositive: isDialogPositive,
        reason: details.reason,
        hasDetails: Boolean(comments),
      },
    });
    return true;
  };

  // NOTE(aman): The dialog stays open until the details are sent, so the
  // thumbs can't change the rating they're being saved on.
  const handleSubmit = async (details: MessageRatingDetails) => {
    setIsSubmitting(true);
    const isSent = await sendDetails(details);
    setIsSubmitting(false);
    if (isSent) setIsDialogOpen(false);
  };

  return (
    <div
      className={clsx(
        '-ml-0.5 flex items-center gap-0.5',
        !isAlwaysVisible &&
          !rating &&
          'opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100'
      )}
    >
      <ThumbButton
        buttonRef={goodButtonRef}
        isPositive
        isDisabled={isDisabled}
        isSelected={rating?.isPositive === true}
        onPress={() => handleThumbPress(true)}
      />
      <ThumbButton
        buttonRef={badButtonRef}
        isPositive={false}
        isDisabled={isDisabled}
        isSelected={rating?.isPositive === false}
        onPress={() => handleThumbPress(false)}
      />
      <MessageFeedbackModal
        isOpen={isDialogOpen}
        isPositive={isDialogPositive}
        isSubmitting={isSubmitting}
        returnFocusRef={isDialogPositive ? goodButtonRef : badButtonRef}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

function ThumbButton({
  buttonRef,
  isPositive,
  isDisabled,
  isSelected,
  onPress,
}: {
  buttonRef: RefObject<HTMLButtonElement | null>;
  isPositive: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  onPress: () => void;
}) {
  const label = isPositive ? 'Good response' : 'Bad response';
  const Icon = isPositive ? ThumbsUp : ThumbsDown;
  return (
    <Button
      ref={buttonRef}
      aria-label={label}
      aria-pressed={isSelected}
      icon={
        <Icon
          size={14}
          className={isSelected ? 'text-gray-700' : 'text-gray-500'}
          fill={isSelected ? 'currentColor' : 'none'}
        />
      }
      isDisabled={isDisabled}
      isGhost
      onPress={onPress}
      size="xs"
      tooltip={label}
    />
  );
}
